import { useCallback, useRef, useState, useEffect } from "react";
import { motionAPI } from "@blings/blings-player/editor-tools";
import { useMst } from "../../../stores/main";
import { pixelToFrame } from "../utils/timelineHelpers";
import { type TimelineLayerInfo } from "../utils/layerHierarchy";
import {
  getLayerTimingCustomMods,
  normalizeAssetId,
} from "../utils/timingModHelpers";
import type { ResolvedTiming } from "./useLayerEdgeDrag";
import type { LayerDragPreviewLayer } from "../../../stores/TimelineStore";
import { computeBodyDragResult } from "./dragUtils";
import { useAnimationJsonEdit } from "./useAnimationJsonEdit";

export interface SelectedLayerInfo {
  layer: TimelineLayerInfo;
  resolvedTiming: ResolvedTiming;
}

interface BodyDragState {
  isDragging: boolean;
  initialIp: number;
  initialOp: number;
  grabOffset: number;
  previewIp: number;
  previewOp: number;
  hasMoved: boolean;
}

const initialDragState: BodyDragState = {
  isDragging: false,
  initialIp: 0,
  initialOp: 0,
  grabOffset: 0,
  previewIp: 0,
  previewOp: 0,
  hasMoved: false,
};

function removeSelectionsCoveredByAncestor(
  selections: SelectedLayerInfo[]
): SelectedLayerInfo[] {
  return selections.filter(
    (selection) =>
      !selections.some(
        (candidate) =>
          candidate !== selection &&
          selection.layer.instancePath.startsWith(
            `${candidate.layer.instancePath}::`
          )
      )
  );
}

export interface UseLayerBodyDragReturn {
  isDragging: boolean;
  previewIp: number | null;
  previewOp: number | null;
  startDrag: (e: React.MouseEvent) => void;
  justDragged: boolean;
}

export function useLayerBodyDrag(
  layer: TimelineLayerInfo,
  compositionStart: number,
  resolvedTiming: ResolvedTiming,
  selectedLayers: SelectedLayerInfo[] = [],
  isLayerSelected: boolean = false
): UseLayerBodyDragReturn {
  const {
    timelineStore,
    modsStore,
    undoStore,
  } = useMst();
  const { zoom, scrollOffset } = timelineStore;
  const { applyAnimationJsonEdit } = useAnimationJsonEdit();

  const [dragState, setDragState] = useState<BodyDragState>(initialDragState);
  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;
  const rafIdRef = useRef<number | null>(null);
  const tracksAreaRef = useRef<HTMLElement | null>(null);
  const capturedLayersRef = useRef<SelectedLayerInfo[]>([]);
  const justDraggedRef = useRef<boolean>(false);

  const startDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      tracksAreaRef.current = (e.currentTarget as HTMLElement).closest(
        ".timeline-tracks-area"
      ) as HTMLElement;

      if (!tracksAreaRef.current) return;

      const rect = tracksAreaRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const clickedFrame =
        pixelToFrame(mouseX, zoom, scrollOffset) + compositionStart;
      const grabOffset = clickedFrame - resolvedTiming.ip;

      undoStore.onGestureStart();

      if (isLayerSelected && selectedLayers.length > 1) {
        capturedLayersRef.current = removeSelectionsCoveredByAncestor(
          selectedLayers
        );
      } else {
        capturedLayersRef.current = [{ layer, resolvedTiming }];
      }

      setDragState({
        isDragging: true,
        initialIp: resolvedTiming.ip,
        initialOp: resolvedTiming.op,
        grabOffset,
        previewIp: resolvedTiming.ip,
        previewOp: resolvedTiming.op,
        hasMoved: false,
      });

      document.body.classList.add("timeline-body-dragging");

      const previewLayers: LayerDragPreviewLayer[] = capturedLayersRef.current.map(
        (info) => ({
          layerUid: info.layer.uid,
          layerName: info.layer.nm,
          assetId: normalizeAssetId(info.layer.assetId),
          instancePath: info.layer.instancePath,
        })
      );
      timelineStore.setLayerDragPreview({
        layers: previewLayers,
        delta: 0,
      });
    },
    [
      compositionStart,
      isLayerSelected,
      layer,
      resolvedTiming,
      scrollOffset,
      selectedLayers,
      timelineStore,
      undoStore,
      zoom,
    ]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const ds = dragStateRef.current;
      if (!ds.isDragging || !tracksAreaRef.current) return;

      if (rafIdRef.current !== null) return;

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        const currentDs = dragStateRef.current;

        const rect = tracksAreaRef.current!.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const currentFrame =
          pixelToFrame(mouseX, zoom, scrollOffset) + compositionStart;

        let newIp = Math.round(currentFrame - currentDs.grabOffset);
        newIp = Math.max(0, newIp);

        const delta = newIp - currentDs.initialIp;
        const newOp = currentDs.initialOp + delta;

        setDragState((prev) => ({
          ...prev,
          previewIp: newIp,
          previewOp: newOp,
          hasMoved: prev.hasMoved || delta !== 0,
        }));

        const previewLayers: LayerDragPreviewLayer[] = capturedLayersRef.current.map(
          (info) => ({
            layerUid: info.layer.uid,
            layerName: info.layer.nm,
            assetId: normalizeAssetId(info.layer.assetId),
            instancePath: info.layer.instancePath,
          })
        );
        timelineStore.setLayerDragPreview({
          layers: previewLayers,
          delta,
        });
      });
    },
    [compositionStart, scrollOffset, timelineStore, zoom]
  );

  const handleMouseUp = useCallback(() => {
    const ds = dragStateRef.current;
    if (!ds.isDragging) return;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    if (!ds.hasMoved) {
      undoStore.onGestureEnd();
      setDragState(initialDragState);
      document.body.classList.remove("timeline-body-dragging");
      timelineStore.clearLayerDragPreview();
      capturedLayersRef.current = [];
      return;
    }

    const delta = ds.previewIp - ds.initialIp;
    const legacyTimingMods = capturedLayersRef.current.flatMap((layerInfo) =>
      getLayerTimingCustomMods(modsStore.mods?.slice() ?? [], layerInfo.layer)
    );

    if (delta === 0) {
      undoStore.onGestureEnd();
      setDragState(initialDragState);
      document.body.classList.remove("timeline-body-dragging");
      timelineStore.clearLayerDragPreview();
      capturedLayersRef.current = [];
      return;
    }

    applyAnimationJsonEdit(
      (workingAnimation) => {
        let hasJsonChanges = false;

        for (const layerInfo of capturedLayersRef.current) {
          const targetLayer = layerInfo.layer;
          const targetResolvedTiming = layerInfo.resolvedTiming;
          const nextTiming = computeBodyDragResult(
            targetResolvedTiming.ip + delta,
            targetResolvedTiming.ip,
            targetResolvedTiming.op
          );

          if (!nextTiming) {
            continue;
          }

          const applied = motionAPI.apply.shiftLayerTimeline(
            workingAnimation as any,
            {
              assetId: normalizeAssetId(targetLayer.assetId),
              layerName: targetLayer.nm,
              layerUid: targetLayer.uid,
            },
            nextTiming.delta,
            {
              includeNestedLayers: false,
            }
          );

          if (applied.changes.length) {
            hasJsonChanges = true;
          }
        }

        return hasJsonChanges ? true : false;
      },
      { manageUndo: false }
    );

    legacyTimingMods.forEach((mod) => {
      modsStore.removeMod(mod);
    });

    undoStore.onGestureEnd();
    justDraggedRef.current = true;
    setTimeout(() => {
      justDraggedRef.current = false;
    }, 0);

    setDragState(initialDragState);
    document.body.classList.remove("timeline-body-dragging");
    timelineStore.clearLayerDragPreview();
    capturedLayersRef.current = [];
  }, [
    applyAnimationJsonEdit,
    modsStore,
    timelineStore,
    undoStore,
  ]);

  useEffect(() => {
    if (!dragState.isDragging) return;

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("timeline-body-dragging");
    };
  }, [dragState.isDragging, handleMouseMove, handleMouseUp]);

  return {
    isDragging: dragState.isDragging,
    previewIp: dragState.isDragging ? dragState.previewIp : null,
    previewOp: dragState.isDragging ? dragState.previewOp : null,
    startDrag,
    justDragged: justDraggedRef.current,
  };
}
